import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
// Forms
import { FormArray, FormBuilder, FormGroup, FormGroupDirective, Validators } from '@angular/forms';
// Controllers
import { AlertController } from '@ionic/angular';

export enum CertificateType {
  A1A3OpenSubCategory = 'A1/A3 Open Sub Category',
  A2OpenSubCategory = 'A2 Open Sub Category',
  StandardScenarioSTS_ES_01 = 'STS-ES-01',
  StandardScenarioSTS_ES_02 = 'STS-ES-02'
}

@Component({
  selector: 'app-pilot-certificate',
  templateUrl: './pilot-certificate.component.html',
  styleUrls: ['./pilot-certificate.component.scss'],
})

/**
 * This component enables a pilot certificate form/info.
 */

export class PilotCertificateComponent  implements OnInit {

  /**
   * Input variables
   */
  @Input() isEditMode: boolean; //Variable controlling the states of the page
  @Input() numCertificates: number; //Variable representing the number of certificates added
  @Input() certificateForms: Array<FormGroup>; // Array of certificates
  @Input() certificateNum: number; //Number i of the certificate
  @Input() pilotCertificate; //Current certificate

  /**
   * Output variables
   */
  @Output() numCertificatesChild = new EventEmitter<number>();
  @Output() certificateFormsChild = new EventEmitter<Array<FormGroup>>();

  /**
   * Variables
   */
  actionCertificate: string = 'createCertificate';//Certificates have got 3 states: create, edit or show.
  PilotForm: FormGroup; //Pilot form

  /**
   * Enum variables
   */
  certificateTypes = Object.values(CertificateType);

  constructor(private rootFormGroup: FormGroupDirective,
    private alertController: AlertController,
    private formBuilder: FormBuilder,) { }

  // #region --> LifeCycle

  /**
   * Every time a certificate is rendered we take the pilot form from the parent and, in case que are editing, we enable the certificate actions corresponding to each state.
   * If a white certificate is received, the registerCertificate state is enabled directly.
   */
  ngOnInit() {

    this.PilotForm = this.rootFormGroup.control;

    if(this.isEditMode){

      if(this.numCertificates<4&&this.certificateNum===this.numCertificates){

        this.actionCertificate="createCertificate";

      } else{

        this.actionCertificate="showCertificate";

      }

    }

    if(this.certificateForms[this.certificateNum].valid){

      this.actionCertificate="showCertificate";

    }
  }

  // #endregion --> LifeCycle

  // #region --> Input/Output Methods

  /**
   * This method is used every time the variable numCertificates is changed in the pilot-certificate component.
   * @param numCertificates : Value sent from the pilot certificate component.
   */
  numCertificatesChildChange(numCertificates: number){
    this.numCertificatesChild.emit(numCertificates);
  }

  /**
   * This method is used every time the certificateFroms array is changed in the pilot-certificate component.
   * @param certificateForms : Value sent from the pilot certificate component.
   */
  certificateFormsChildChange(certificateForms: Array<FormGroup>){
    this.certificateFormsChild.emit(certificateForms);
  }

  // #endregion --> Input/Output Methods

  // #region --> Certificate Methods

  /**
   * This method is used when we want to add a certificate to our list. It checks its consistency and updates all the variables.
   */
  async createCertificate(){
    var duplicate: boolean = false;

    //Check if we add more than 4 certificates (4 is the max number)
    if(this.numCertificates>4){
      const alert = await this.alertController.create({
        header: 'Number of certificates not valid',
        message: 'It is not allowed to add more certificates than the ones enabled.',
        buttons: ['OK'],
      });

      await alert.present();
    }

    //Check for duplicates
    for(let i=0; i<this.certificateForms.length-1; i++){
      if(this.certificateForms[i].get('certificateName').value===this.certificateForms[this.certificateForms.length-1].get('certificateName').value){
        const alert = await this.alertController.create({
          header: 'Certificate already added',
          message: 'You can not add an existing certificate. If you want to edit one, please use the edit certificate button.',
          buttons: ['OK'],
        });
        await alert.present();
        duplicate=true;
      }
    }

    // If no duplicates are found, check consistency and add it to the certificates array.
    if(!duplicate){

      const lastCertificate = this.certificateForms[this.certificateForms.length-1];

      if(lastCertificate.valid){

        this.actionCertificate="showCertificate";

        if((this.certificateNum===0)&&(lastCertificate.get('certificateName').value==="Standard Scenario STS-ES-01" || lastCertificate.get('certificateName').value==="Standard Scenario STS-ES-02")){
          const alert = await this.alertController.create({
            header: 'Automatically generate certificates ',
            message: 'Do you want to automatically generate the A1/A3 Open Sub Category and A2 Open Sub Category certificates?',
            buttons: [
              {
                text: 'Cancel',
                role: 'cancel',
              },
              {
                text: 'Yes',
                handler: () => {
                  this.numCertificates=this.numCertificates+1;

                  const newCertificate1 = this.formBuilder.group({
                    certificateName: ['A1/A3 Open Sub Category',Validators.required],
                    certificateIssuer: [lastCertificate.get('certificateIssuer').value,Validators.required],
                    licenseNumber: [lastCertificate.get('licenseNumber').value,Validators.required],
                    expirationDate: [lastCertificate.get('expirationDate').value,Validators.required],
                    country: [lastCertificate.get('country').value,Validators.required]
                  });

                  (this.PilotForm.get('certificateList') as FormArray).push(newCertificate1)
                  this.certificateForms.push(newCertificate1);
                  this.numCertificates=this.numCertificates+1;

                  const newCertificate2 = this.formBuilder.group({
                    certificateName: ['A2 Open Sub Category',Validators.required],
                    certificateIssuer: [lastCertificate.get('certificateIssuer').value,Validators.required],
                    licenseNumber: [lastCertificate.get('licenseNumber').value,Validators.required],
                    expirationDate: [lastCertificate.get('expirationDate').value,Validators.required],
                    country: [lastCertificate.get('country').value,Validators.required]
                  });

                  (this.PilotForm.get('certificateList') as FormArray).push(newCertificate2)
                  this.certificateForms.push(newCertificate2);
                  this.numCertificates=this.numCertificates+1;

                  if(this.numCertificates<4){
                    const newCertificate = this.formBuilder.group({
                      certificateName: ['',Validators.required],
                      certificateIssuer: ['',Validators.required],
                      licenseNumber: ['',Validators.required],
                      expirationDate: ['',Validators.required],
                      country: ['',Validators.required]
                    });
                    (this.PilotForm.get('certificateList') as FormArray).push(newCertificate)
                    this.certificateForms.push(newCertificate);
                  }

                  this.certificateFormsChildChange(this.certificateForms);
                  this.numCertificatesChildChange(this.numCertificates);
                },
              },
            ],
          });
          await alert.present();

        } else{

          this.numCertificates=this.numCertificates+1;

          if(this.numCertificates<4){
            const newCertificate = this.formBuilder.group({
              certificateName: ['',Validators.required],
              certificateIssuer: ['',Validators.required],
              licenseNumber: ['',Validators.required],
              expirationDate: ['',Validators.required],
              country: ['',Validators.required]
            });

            (this.PilotForm.get('certificateList') as FormArray).push(newCertificate)
            this.certificateForms.push(newCertificate);

          }

          this.certificateFormsChildChange(this.certificateForms);
          this.numCertificatesChildChange(this.numCertificates);
        }
      } else{

        const alert = await this.alertController.create({
          header: 'Invalid certificate',
          message: 'Values are not well defined.',
          buttons: ['OK'],
        });
        await alert.present();
        duplicate=true;

      }
    }
  }

  /**
   * This methos is used when we want to edit an existing certificate.
   */
  async editCertificate(){
    var duplicate: boolean = false;

    // Check for duplicates
    if(this.actionCertificate==='editCertificate'){
      for(let i=0; i<this.certificateForms.length-1; i++){
        if(this.certificateForms[i].get('certificateName').value===this.certificateForms[this.certificateNum].get('certificateName').value && this.certificateNum!==i){
          const alert = await this.alertController.create({
            header: 'Certificate already added',
            message: 'You can not add an existing certificate. If you want to edit one, please use the edit certificate button.',
            buttons: ['OK'],
          });

          await alert.present();

          duplicate=true;
        }
      }
    }

    // If no duplicates found, check consistency and update certificate.
    if(!duplicate){
      if(this.certificateForms[this.certificateNum].valid && this.actionCertificate==='editCertificate'){
        this.actionCertificate='showCertificate';
        if(this.isEditMode){
          this.actionCertificate='showCertificate';
        }
      }
      else{
        const alert = await this.alertController.create({
          header: 'Invalid certificate',
          message: 'Values are not well defined.',
          buttons: ['OK'],
        });
        await alert.present();
        duplicate=true;
      }
      this.certificateFormsChildChange(this.certificateForms);
      this.numCertificatesChildChange(this.numCertificates);
    }
  }

  /**
   * This method is used when we want to delete a certificate.
   */
  async deleteCertificate(){
    const deletedCertificate = this.certificateForms[this.certificateNum];
    const alert = await this.alertController.create({
      header: 'Delete '+deletedCertificate.get('certificateName').value,
      message: 'Are you sure you want to delete the vehicle '+deletedCertificate.get('certificateName').value+'?',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Yes',
          handler: () => {
            (this.PilotForm.get('certificateList') as FormArray).removeAt(this.certificateNum);
            this.certificateForms.splice(this.certificateNum,1);

            const newCertificate = this.formBuilder.group({
              certificateName: ['',Validators.required],
              certificateIssuer: ['',Validators.required],
              licenseNumber: ['',Validators.required],
              expirationDate: ['',Validators.required],
              country: ['',Validators.required]
            });

            if(this.numCertificates===4){
              (this.PilotForm.get('certificateList') as FormArray).push(newCertificate)
              this.certificateForms.push(newCertificate);
            }

            this.numCertificates=this.certificateForms.length-1;

            this.certificateFormsChildChange(this.certificateForms);
            this.numCertificatesChildChange(this.numCertificates);
          },
        },
      ],
    });
    await alert.present();
    this.actionCertificate="showCertificate";
  }

  /**
   * This method is used when we want to change from showCertificate state to editCertificate.
   */
  showEditCertificate(){
    this.actionCertificate="editCertificate";
  }

  // #endregion --> Certificates Methods

  // #region --> Date Methods

  /**
   * This method is used when an expiration date is introduced and checks if the format is correct.
   * @param ev : expiration date introduced
   */
  async expirationDateChange(ev: any){

    const pattern = /^(?:(?:31(\/|-|\.)(?:0?[13578]|1[02]))\1|(?:(?:29|30)(\/|-|\.)(?:0?[13-9]|1[0-2])\2))(?:(?:1[6-9]|[2-9]\d)?\d{2})$|^(?:29(\/|-|\.)0?2\3(?:(?:(?:1[6-9]|[2-9]\d)?(?:0[48]|[2468][048]|[13579][26])|(?:(?:16|[2468][048]|[3579][26])00))))$|^(?:0?[1-9]|1\d|2[0-8])(\/|-|\.)(?:(?:0?[1-9])|(?:1[0-2]))\4(?:(?:1[6-9]|[2-9]\d)?\d{2})$/;//Regex in order to detect if the string input is a valid date format

    if(!pattern.test(ev.detail.value)){
      const alert = await this.alertController.create({
        header: 'Invalid date',
        message: 'Please, enter a date with the format DD/MM/YYYY like 12/11/2021. Also check if the date you have entered exists.',
        buttons: [
            {
            text: 'OK',
            handler: () => {
              this.certificateForms[this.certificateNum].get('expirationDate').patchValue('');
            }
          }
        ]
      });
          await alert.present();
    }

  }

  // #endregion --> Date Methods

}
